'use client';

import * as React from 'react';
import { Suspense } from 'react';
import { useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { createClient } from '@/lib/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardDescription } from '@/components/ui/card';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupButton,
} from '@/components/ui/input-group';
import { Chrome, Eye, EyeOff, Loader2, Mail } from 'lucide-react';

function LoginContent() {
  const t = useTranslations('login');
  const supabase = createClient();
  const searchParams = useSearchParams();
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [message, setMessage] = React.useState<string | null>(null);
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [showPassword, setShowPassword] = React.useState(false);
  const [mode, setMode] = React.useState<'signin' | 'signup' | 'forgot'>('signin');

  React.useEffect(() => {
    const requestedMode = searchParams.get('mode');
    const requestedEmail = searchParams.get('email');

    if (requestedMode === 'signup' || requestedMode === 'forgot' || requestedMode === 'signin') {
      setMode(requestedMode);
    }

    if (requestedEmail) {
      setEmail(requestedEmail);
    }
  }, [searchParams]);

  // Build the post-auth destination URL from query params
  // Supports: ?redirect=/onboarding&plan=growth&cadence=annual
  //
  // Flow: Pricing page → /login?redirect=/onboarding&plan=growth&cadence=annual
  //       → Google OAuth → /auth/callback?next=/onboarding?plan=growth&cadence=annual
  //       → Redirect to /onboarding?plan=growth&cadence=annual
  const buildRedirectUrl = () => {
    const redirect = searchParams.get('redirect');
    const returnUrl = searchParams.get('returnUrl');
    const plan = searchParams.get('plan');
    const cadence = searchParams.get('cadence');

    // Default to /org/select if no redirect specified
    if (!redirect && !returnUrl) {
      return '/org/select';
    }

    const target = redirect || returnUrl || '/org/select';

    // Build the destination URL with plan/cadence params
    const destinationUrl = new URL(target, window.location.origin);
    if (plan) destinationUrl.searchParams.set('plan', plan);
    if (cadence) destinationUrl.searchParams.set('cadence', cadence);

    // Return just the path + query string (not the full URL)
    return destinationUrl.pathname + destinationUrl.search;
  };

  const clearFeedback = () => {
    setError(null);
    setMessage(null);
  };

  const handleEmailAuth = async (event: React.FormEvent) => {
    event.preventDefault();
    clearFeedback();

    if (!email || !email.includes('@')) {
      setError(t('errors.invalidEmail'));
      return;
    }

    if (mode === 'forgot') {
      setLoading(true);
      try {
        const redirectTo = `${window.location.origin}/reset-password`;
        const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo,
        });

        if (resetError) {
          setError(resetError.message);
          return;
        }

        setMessage(t('success.resetEmailSent'));
      } catch {
        setError(t('errors.resetFailed'));
      } finally {
        setLoading(false);
      }
      return;
    }

    if (!password || password.length < 8) {
      setError(t('errors.passwordTooShort'));
      return;
    }

    if (mode === 'signup' && password !== confirmPassword) {
      setError(t('errors.passwordsDontMatch'));
      return;
    }

    setLoading(true);
    try {
      const destination = buildRedirectUrl();

      if (mode === 'signup') {
        const hasExplicitDestination = Boolean(
          searchParams.get('redirect') || searchParams.get('returnUrl'),
        );
        const { data, error: signupError } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/auth/callback?next=${encodeURIComponent(destination)}`,
          },
        });

        if (signupError) {
          setError(signupError.message);
          return;
        }

        if (data.session && hasExplicitDestination) {
          window.location.href = destination;
          return;
        }

        setMessage(t('success.accountCreated'));
        setMode('signin');
        return;
      }

      const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
      if (signInError) {
        setError(signInError.message);
        return;
      }

      window.location.href = destination;
    } catch {
      setError(t('errors.authFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    clearFeedback();

    try {
      const destination = buildRedirectUrl();
      // Store the post-auth destination in a short-lived cookie.
      // We cannot pass ?next= in the OAuth redirectTo because Supabase validates
      // the full URL against its allowed redirect list — query params make it a
      // different URL that isn't registered, causing Supabase to fall back to
      // the configured Site URL (production). The cookie is read by the callback
      // route server-side as a fallback when no ?next= param is present.
      document.cookie = `oauth_next=${encodeURIComponent(destination)}; path=/; max-age=300; SameSite=Lax`;
      const redirectTo = `${window.location.origin}/auth/callback`;
      const { error: oauthError } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo,
        },
      });

      if (oauthError) {
        setError(oauthError.message);
      }
    } catch (err) {
      setError(t('errors.googleFailed'));
    } finally {
      // Keep loading true since we're redirecting; if we get here, something went wrong
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen flex items-center justify-center p-4 bg-background">
      <Card className="w-full max-w-sm">
        <CardHeader className="space-y-1">
          <h1 className="text-2xl font-bold text-center">{t('welcome')}</h1>
          <CardDescription className="text-center">
            {mode === 'signup'
              ? t('signupDescription')
              : mode === 'forgot'
                ? t('forgotDescription')
                : t('signinDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <form onSubmit={handleEmailAuth} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t('email')}</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder={t('emailPlaceholder')}
                disabled={loading}
                className="h-10"
              />
            </div>

            {mode !== 'forgot' && (
              <div className="space-y-2">
                <Label htmlFor="password">{t('password')}</Label>
                <InputGroup className="h-10">
                  <InputGroupInput
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    placeholder={t('passwordPlaceholder')}
                    disabled={loading}
                    className="h-full"
                  />
                  <InputGroupAddon align="inline-end">
                    <InputGroupButton
                      size="icon-sm"
                      onClick={() => setShowPassword((prev) => !prev)}
                      aria-label={showPassword ? t('hidePassword') : t('showPassword')}
                      aria-pressed={showPassword}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </InputGroupButton>
                  </InputGroupAddon>
                </InputGroup>
              </div>
            )}

            {mode === 'signup' && (
              <div className="space-y-2">
                <Label htmlFor="confirm-password">{t('confirmPassword')}</Label>
                <InputGroup className="h-10">
                  <InputGroupInput
                    id="confirm-password"
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(event) => setConfirmPassword(event.target.value)}
                    placeholder={t('confirmPasswordPlaceholder')}
                    disabled={loading}
                    className="h-full"
                  />
                  <InputGroupAddon align="inline-end">
                    <InputGroupButton
                      size="icon-sm"
                      onClick={() => setShowPassword((prev) => !prev)}
                      aria-label={showPassword ? t('hidePassword') : t('showPassword')}
                      aria-pressed={showPassword}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </InputGroupButton>
                  </InputGroupAddon>
                </InputGroup>
              </div>
            )}

            <Button type="submit" disabled={loading} className="w-full" variant="outline" size="lg">
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  {mode === 'signup'
                    ? t('creatingAccount')
                    : mode === 'forgot'
                      ? t('sendingResetLink')
                      : t('signingIn')}
                </>
              ) : mode === 'signup' ? (
                <>
                  <Mail className="mr-2 h-5 w-5" />
                  {t('createAccountWithEmail')}
                </>
              ) : mode === 'forgot' ? (
                t('sendResetLink')
              ) : (
                <>
                  <Mail className="mr-2 h-5 w-5" />
                  {t('signInWithEmail')}
                </>
              )}
            </Button>
          </form>

          {mode !== 'forgot' && (
            <>
              <div className="relative py-1 text-center text-xs uppercase text-muted-foreground">
                <span className="bg-card px-2">{t('or')}</span>
              </div>
              <Button
                onClick={handleGoogleSignIn}
                disabled={loading}
                className="w-full"
                variant="default"
                size="lg"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    {t('signingIn')}
                  </>
                ) : (
                  <>
                    <Chrome className="mr-2 h-5 w-5" />
                    {mode === 'signup' ? t('signUpWithGoogle') : t('signInWithGoogle')}
                  </>
                )}
              </Button>
            </>
          )}

          {message && (
            <div className="p-3 text-sm text-emerald-700 bg-emerald-100 rounded-md">{message}</div>
          )}

          {error && (
            <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-md">{error}</div>
          )}

          <div className="space-y-2 text-center text-sm">
            {mode !== 'signin' && (
              <Button
                className="text-muted-foreground hover:text-foreground"
                onClick={() => {
                  clearFeedback();
                  setMode('signin');
                }}
                variant="link"
              >
                {t('backToSignIn')}
              </Button>
            )}

            {mode === 'signin' && (
              <div className="flex justify-center gap-3">
                <button
                  type="button"
                  className="text-muted-foreground hover:text-foreground"
                  onClick={() => {
                    clearFeedback();
                    setMode('signup');
                  }}
                >
                  {t('createAccount')}
                </button>
                <span className="text-muted-foreground">·</span>
                <button
                  type="button"
                  className="text-muted-foreground hover:text-foreground"
                  onClick={() => {
                    clearFeedback();
                    setMode('forgot');
                  }}
                >
                  {t('forgotPassword')}
                </button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </main>
  );
}

export default function LoginPage() {
  return (
    <Suspense
      fallback={
        <main className="min-h-screen flex items-center justify-center p-4 bg-background">
          <Card className="w-full max-w-sm">
            <CardHeader className="space-y-1">
              <h1 className="text-2xl font-bold text-center">Bringtable</h1>
              <CardDescription className="text-center">Loading...</CardDescription>
            </CardHeader>
            <CardContent className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </CardContent>
          </Card>
        </main>
      }
    >
      <LoginContent />
    </Suspense>
  );
}
